import { Info, Loader2, Pencil, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useDestinations } from '@/hooks/useDestinations';
import type {
  DestinationOption,
  DestinationScope,
} from '@/types/provider-destinations';
import { ResearchStatus } from '@/types/provider-destinations';

import { DestinationOptionCard } from './DestinationOptionCard';
import { SCOPE_LABELS } from './utils';

interface DestinationPickerProps {
  scope: DestinationScope;
  providerServiceId: string;
  onSelectDestination: (option: DestinationOption) => void;
  onOpenSearch: () => void;
  onOpenManualEntry: () => void;
  allowNoDestination?: boolean;
  onSelectNoDestination?: () => void;
}

export const DestinationPicker = ({
  scope,
  providerServiceId,
  onSelectDestination,
  onOpenSearch,
  onOpenManualEntry,
  allowNoDestination = false,
  onSelectNoDestination,
}: DestinationPickerProps) => {
  const { getScopeDestinations, isLoading } =
    useDestinations(providerServiceId);
  const block = getScopeDestinations(scope);
  const renderActions = () => (
    <div className="flex items-center gap-3 border-t pt-3">
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpenSearch}
        className="gap-1.5 text-blue-700 transition-all duration-200 hover:-translate-y-0.5 hover:text-blue-800 active:translate-y-0 active:scale-95"
      >
        <Search className="size-3.5" />
        Search tool
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpenManualEntry}
        className="gap-1.5 text-blue-700 transition-all duration-200 hover:-translate-y-0.5 hover:text-blue-800 active:translate-y-0 active:scale-95"
      >
        <Pencil className="size-3.5" />
        Enter manually
      </Button>
      {allowNoDestination && onSelectNoDestination && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onSelectNoDestination}
          className="text-zinc-700 transition-all duration-200 hover:-translate-y-0.5 hover:text-zinc-900 active:translate-y-0 active:scale-95"
        >
          Skip contact info
        </Button>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!block) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-3">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 size-4 shrink-0 text-zinc-600" />
            <div>
              <p className="text-sm font-medium text-zinc-900">
                No destination data available for {SCOPE_LABELS[scope]}.
              </p>
              <p className="mt-0.5 text-xs text-zinc-600">
                Add one now using Search tool or Enter manually.
              </p>
            </div>
          </div>
        </div>
        {renderActions()}
      </div>
    );
  }

  const { recommended, alternates, research_status } = block;
  const alternateOptions = alternates ?? [];
  const isResearching = research_status === ResearchStatus.IN_PROGRESS;

  return (
    <div className="space-y-4">
      {isResearching && (
        <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
          <Loader2 className="size-3.5 animate-spin" />
          <span>Research in progress&hellip;</span>
        </div>
      )}

      {recommended && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
            Recommended
          </p>
          <DestinationOptionCard
            option={recommended}
            onSelect={onSelectDestination}
            isRecommended
          />
        </div>
      )}

      {alternateOptions.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
            Other known options
          </p>
          <div className="space-y-2">
            {alternateOptions.map((alt) => (
              <DestinationOptionCard
                key={alt.contact_point_id}
                option={alt}
                onSelect={onSelectDestination}
              />
            ))}
          </div>
        </div>
      )}

      {!recommended && alternateOptions.length === 0 && !isResearching && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-3">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 size-4 shrink-0 text-zinc-600" />
            <div>
              <p className="text-sm font-medium text-zinc-900">
                No contact info is set yet.
              </p>
              <p className="mt-0.5 text-xs text-zinc-600">
                Add one now using Search tool or Enter manually below.
              </p>
            </div>
          </div>
        </div>
      )}

      {renderActions()}
    </div>
  );
};
